import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useMemo,
} from "react";
import { IAllocation } from "../App";
import { ParsedDatesRange } from "../utils/getDatesRange";
import dayjs from "dayjs";
import secureLocalStorage from "react-secure-storage";
import { IKeyValue } from "../../@types/key-value.type";

interface AllocationContextType {
  allocations: IAllocation[];
  setAllocations: React.Dispatch<React.SetStateAction<IAllocation[]>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  range: ParsedDatesRange;
  setRange: React.Dispatch<React.SetStateAction<ParsedDatesRange>>;
  setTotalAllocations: React.Dispatch<React.SetStateAction<number>>;
  totalAllocations: number;
  valueCategories: string;
  setValueCategories: React.Dispatch<React.SetStateAction<string>>;
  searchResource: string;
  setSearchResource: React.Dispatch<React.SetStateAction<string>>;
  projectSelected: IKeyValue[];
  setProjectSelected: React.Dispatch<React.SetStateAction<IKeyValue[]>>;
  clientSelected: IKeyValue[];
  setClientSelected: React.Dispatch<React.SetStateAction<IKeyValue[]>>;
  positionSelected: IKeyValue[];
  setPositionSelected: React.Dispatch<React.SetStateAction<IKeyValue[]>>;
  areaSelected: IKeyValue[];
  costCenterSelected: IKeyValue[];
  setAreaSelected: React.Dispatch<React.SetStateAction<IKeyValue[]>>;
  setCostCenterSelected: React.Dispatch<React.SetStateAction<IKeyValue[]>>;
  getNumberValueAndIdFromCategories: () => {
    numberValueCategories: number;
    idCategorieArray: IKeyValue[] | undefined;
  };
  hasFilters: boolean;
}

const AllocationContext = createContext<AllocationContextType | undefined>(
  undefined
);

export const useAllocation = (): AllocationContextType => {
  const context = useContext(AllocationContext);
  if (!context) {
    throw new Error(
      "useAllocation precisa ser usando dentro de um AllocationProvider"
    );
  }
  return context;
};

export const AllocationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [allocations, setAllocations] = useState<IAllocation[]>([]);
  const [totalAllocations, setTotalAllocations] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [range, setRange] = useState<ParsedDatesRange>({
    startDate: dayjs().startOf("month").toDate(),
    endDate: dayjs().toDate(),
  });

  const [valueCategories, setValueCategories] = useState(
    () =>
      JSON.parse(
        secureLocalStorage.getItem("searchParamsAllocationMap")?.toString() ||
          "{}"
      ).valueCategoriesStorage || "0"
  );
  const [searchResource, setSearchResource] = useState<string>(
    () =>
      JSON.parse(
        secureLocalStorage.getItem("searchParamsAllocationMap")?.toString() ||
          "{}"
      ).searchResourceStorage || ""
  );
  const [projectSelected, setProjectSelected] = useState<IKeyValue[]>(
    () =>
      JSON.parse(
        secureLocalStorage.getItem("searchParamsAllocationMap")?.toString() ||
          "{}"
      ).projectSelectedStorage || ""
  );
  const [clientSelected, setClientSelected] = useState<IKeyValue[]>(
    () =>
      JSON.parse(
        secureLocalStorage.getItem("searchParamsAllocationMap")?.toString() ||
          "{}"
      ).clientSelectedStorage || ""
  );
  const [positionSelected, setPositionSelected] = useState<IKeyValue[]>(
    () =>
      JSON.parse(
        secureLocalStorage.getItem("searchParamsAllocationMap")?.toString() ||
          "{}"
      ).positionSelectedStorage || ""
  );
  const [areaSelected, setAreaSelected] = useState<IKeyValue[]>(
    () =>
      JSON.parse(
        secureLocalStorage.getItem("searchParamsAllocationMap")?.toString() ||
          "{}"
      ).areSelectedStorage || ""
  );
  const [costCenterSelected, setCostCenterSelected] = useState<IKeyValue[]>(
    () =>
      JSON.parse(
        secureLocalStorage.getItem("searchParamsAllocationMap")?.toString() ||
          "{}"
      ).costCenterSelectedStorage || ""
  );

  const hasFilters = useMemo(() => {
    return (
      searchResource !== "" ||
      areaSelected.length > 0 ||
      clientSelected.length > 0 ||
      projectSelected.length > 0 ||
      positionSelected.length > 0 ||
      costCenterSelected.length > 0
    );
  }, [
    searchResource,
    areaSelected,
    clientSelected,
    projectSelected,
    positionSelected,
    costCenterSelected,
  ]);

  const getNumberValueAndIdFromCategories = () => {
    let idCategorieArray;
    let numberValueCategories = Number(valueCategories);

    if (numberValueCategories > 1) {
      switch (valueCategories) {
        case "2":
          idCategorieArray = projectSelected;
          break;
        case "3":
          idCategorieArray = clientSelected;
          break;
        case "4":
          idCategorieArray = positionSelected;
          break;
        case "5":
          idCategorieArray = areaSelected;
          break;
        case "6":
          idCategorieArray = costCenterSelected;
          break;
        default:
          idCategorieArray = undefined;
      }
    }

    if (numberValueCategories > 1 && !idCategorieArray)
      numberValueCategories = 0;

    return {
      numberValueCategories,
      idCategorieArray,
    };
  };
  const filteredData = useMemo(
    () =>
      allocations.map((person) => ({
        ...person,
        data: person.data,
      })),
    [allocations, range]
  );

  return (
    <AllocationContext.Provider
      value={{
        allocations: filteredData,
        setAllocations,
        isLoading,
        setIsLoading,
        range,
        setRange,
        setTotalAllocations,
        totalAllocations,
        areaSelected,
        costCenterSelected,
        setAreaSelected,
        setCostCenterSelected,
        clientSelected,
        setClientSelected,
        getNumberValueAndIdFromCategories,
        positionSelected,
        setPositionSelected,
        projectSelected,
        setProjectSelected,
        searchResource,
        setSearchResource,
        setValueCategories,
        valueCategories,
        hasFilters,
      }}
    >
      {children}
    </AllocationContext.Provider>
  );
};
